import React, { useState, useEffect } from 'react';
import { formatEuro } from '../utils/currency';
import { formatDate } from '../utils/date';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { InvoiceDto, connectInvoiceToHomeExpense, disconnectInvoiceFromHomeExpense } from '../services/expensesApi';
import { HomeExpenseDto } from '../types/homeExpenses';
import { getHomeExpenses } from '../services/homeExpensesApi';

interface InvoiceHomeExpenseConnectionProps {
  invoice: InvoiceDto;
  onConnectionChanged: () => void;
  variant?: 'button' | 'dialog';
}

interface HomeExpenseOption {
  publicId: string;
  date: string;
  group: string;
  amount: number;
  account: string;
  observations?: string;
  isIncome: boolean;
  label: string;
}

const InvoiceHomeExpenseConnection: React.FC<InvoiceHomeExpenseConnectionProps> = ({
  invoice,
  onConnectionChanged,
  variant = 'button'
}) => {
  const [open, setOpen] = useState(false);
  const [homeExpenses, setHomeExpenses] = useState<HomeExpenseOption[]>([]);
  const [selectedHomeExpense, setSelectedHomeExpense] = useState<HomeExpenseOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingHomeExpenses, setLoadingHomeExpenses] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      fetchHomeExpenses();
    }
  }, [open]);

  const fetchHomeExpenses = async () => {
    try {
      setLoadingHomeExpenses(true);
      const expenses = await getHomeExpenses();
      
      // Filter only actual expenses (negative amounts) and add label for autocomplete
      const expenseOptions: HomeExpenseOption[] = expenses
        .filter((expense: HomeExpenseDto) => !expense.isIncome) // Only expenses, not income
        .map((expense: HomeExpenseDto) => ({
          publicId: expense.publicId,
          date: expense.date,
          group: expense.group,
          amount: expense.amount,
          account: expense.account,
          observations: expense.observations,
          isIncome: expense.isIncome,
          label: `${formatDate(expense.date)} - ${expense.group} - ${formatEuro(Math.abs(expense.amount))} - ${expense.account}${expense.observations ? ` - ${expense.observations}` : ''}`
        }))
        .sort((a: HomeExpenseOption, b: HomeExpenseOption) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort by date descending

      setHomeExpenses(expenseOptions);
    } catch (err) {
      console.error('Error fetching home expenses:', err);
      setError('Não foi possível carregar as despesas da casa');
    } finally {
      setLoadingHomeExpenses(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedHomeExpense) return;

    try {
      setLoading(true);
      setError(null);
      
      await connectInvoiceToHomeExpense(invoice.publicId, selectedHomeExpense.publicId);
      
      setOpen(false);
      setSelectedHomeExpense(null);
      onConnectionChanged();
    } catch (err) {
      console.error('Error connecting invoice to home expense:', err);
      setError('Não foi possível associar a fatura à despesa da casa');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await disconnectInvoiceFromHomeExpense(invoice.publicId);
      
      onConnectionChanged();
    } catch (err) {
      console.error('Error disconnecting invoice from home expense:', err);
      setError('Não foi possível desassociar a fatura da despesa da casa');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedHomeExpense(null);
    setError(null);
  };

  if (variant === 'button') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {invoice.homeExpensePublicId ? (
          <>
            <Chip
              label={invoice.homeExpenseDescription || 'Associada a despesa da casa'}
              color="success"
              size="small"
              icon={<LinkIcon />}
            />
            <Tooltip title="Desassociar da despesa da casa">
              <IconButton
                size="small"
                onClick={handleDisconnect}
                disabled={loading}
                color="error"
              >
                {loading ? <CircularProgress size={16} /> : <LinkOffIcon />}
              </IconButton>
            </Tooltip>
          </>
        ) : (
          <Tooltip title="Associar a despesa da casa">
            <IconButton
              size="small"
              onClick={() => setOpen(true)}
              disabled={loading}
              color="primary"
            >
              {loading ? <CircularProgress size={16} /> : <LinkIcon />}
            </IconButton>
          </Tooltip>
        )}

        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>Associar fatura a despesa da casa</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Detalhes da fatura
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {formatDate(invoice.date)} - {formatEuro(invoice.total)}
                {invoice.notes && ` - ${invoice.notes}`}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Autocomplete
                options={homeExpenses}
                getOptionLabel={(option) => option.label}
                value={selectedHomeExpense}
                onChange={(_, newValue) => setSelectedHomeExpense(newValue)}
                loading={loadingHomeExpenses}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecionar despesa da casa"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingHomeExpenses ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2">
                        {formatDate(option.date)} - {option.group} - {formatEuro(Math.abs(option.amount))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.account}{option.observations && ` - ${option.observations}`}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText={loadingHomeExpenses ? "A carregar..." : "Nenhuma despesa da casa encontrada"}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancelar</Button>
            <Button
              onClick={handleConnect}
              disabled={!selectedHomeExpense || loading}
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              Associar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return null;
};

export default InvoiceHomeExpenseConnection;
